import _ from "lodash";
import React, { useEffect } from "react";
import { SearchList } from "../SearchList/SearchList";
import classes from "./SearchResult.scss";
import withLoader from "UIComponents/GqlWithLoader";
import EmptyView from "UIComponents/EmptyView";
import I18nHOC from "UIComponents/I18nHOC";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { getGlobalSearchResult } from "../../modules/GlobalSearchBarQueries";
import { getGlobalSearchResultFromCache } from "../../modules/GlobalSearchBarHelper";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import {
  typesConfig,
  getParseResultMemoized,
  getFilterDataMemoized,
  MODULE_FOR_SORT_OVERRIDE,
} from "../Utils";

import elasticlunr from "elasticlunr";

const style = {
  emptyViewStyle: {
    containerStyle: {
      padding: "unset",
    },
  },
};

const COUNT_OF_FIRST_N_RESULT = 40;

//first clear stop words in our case who , we ,are might be come in Unit plan and LE
elasticlunr.clearStopWords();

//initialize instance of elasticlunr
const index = elasticlunr(function () {
  this.addField("title");
  this.addField("category");
  this.addField("acronym");
  this.addField("email");
  this.setRef("id");

  // remove stemmer from pipeline to allow matching partial words,
  // refer to https://github.com/weixsong/elasticlunr.js/issues/16
  this.pipeline.remove(elasticlunr.stemmer);
});

const addAllDataToIndex = ({ allData }) => {
  _.forEach(allData, data => index.addDoc(data));
};

const addChangedDataToIndex = ({ changedData }) => {
  _.forEach(changedData, data => index.addDoc(data));
};

const getDataMemoizationParams = ({ params, dataKey }) => {
  const data = _.get(params, dataKey, []);
  const changedDataIds = _.map(data, "id");
  return JSON.stringify(changedDataIds);
};

const addAllDataToIndexMemoized = _.memoize(
  params => addAllDataToIndex(params),
  // params => JSON.stringify(params)
  params => getDataMemoizationParams({ params, dataKey: "allData" })
);

const addAllChangedDataToIndexMemoized = _.memoize(
  params => addChangedDataToIndex(params),
  // params => JSON.stringify(params)
  params => getDataMemoizationParams({ params, dataKey: "changedData" })
);

const performSearchSortOverride = result => {
  const { type } = result;

  if (_.includes(MODULE_FOR_SORT_OVERRIDE, type)) {
    return 1;
  }
  return;
};

export const SearchResults = props => {
  const {
    suggestions = [],
    localSuggestions = [],
    term,
    onSuggestionClick,
    t,
  } = props;

  //merge data by id if suggestions from local and backend r same
  const mergedData = _.merge(
    _.keyBy(suggestions, "id"),
    _.keyBy(localSuggestions, "id")
  );

  addAllDataToIndexMemoized({ allData: localSuggestions });

  addAllChangedDataToIndexMemoized({ changedData: suggestions });

  const searchResult = index.search(term, {
    fields: {
      title: { boost: 2 },
      category: { boost: 1 },
      acronym: { boost: 2 },
      email: { boost: 2 },
    },
    expand: true,
  });

  const totalData = _.chain(searchResult)
    .reduce((result, data) => {
      const indexedData = mergedData[data.ref];

      if (indexedData) {
        indexedData.score = data.score;
        result.push(indexedData);
      }
      return result;
    }, [])
    .orderBy(result => result.order, "asc")
    .orderBy(result => result.score, "desc")
    .sortBy(result => performSearchSortOverride(result))
    .splice(0, COUNT_OF_FIRST_N_RESULT)
    .value();

  useEffect(() => {
    document.querySelector("[data-elm=search-item]")?.focus();
  }, [totalData]);

  return (
    <div className={classes.searchListContainer}>
      {!_.get(totalData, "length", 0) ? (
        <div className={classes.emptyResult}>
          <EmptyView
            emptyImageUrl={NoSearchResultsIllustration}
            size={"small"}
            emptyHeaderText={`${t("common:no_results")}!`}
            emptyText={t("integration:try_modifying_your_search")}
            {...style.emptyViewStyle}
          />
        </div>
      ) : (
        <SearchList
          data={totalData}
          onSelectItem={onSuggestionClick}
          searchText={term}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { module, term } = ownProps;

  const filters = getFilterDataMemoized({
    state: state.platform,
    searchText: term,
    module,
  });

  return {
    filters,
    term,
    module,
  };
};

export default compose(
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, null),
  graphql(getGlobalSearchResult, {
    name: "getGlobalSearchResultQuery",
    options: ({ filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: filters,
    }),
    skip: props => {
      const { module } = props;
      return module == typesConfig.MODULES.id;
    },
    props: ({
      getGlobalSearchResultQuery,
      ownProps: { isData, isLoading, filters, t },
    }) => {
      const result = getGlobalSearchResultFromCache({
        filters,
      });

      const queryResult = _.get(result, "platform.platformSearch", []);

      const suggestions = getParseResultMemoized({
        result: queryResult,
        t,
      });

      return {
        suggestions,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          _.includes([1, 2], getGlobalSearchResultQuery["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(SearchResults);
